import React, { useCallback, useEffect, useState } from "react";
import { FaPlug } from "react-icons/fa";
import { SectionCard } from "../SectionCard";
import { useInView } from "../useInView";
import { fetchOrgIntegrations } from "../api";
import { formatRelative } from "../format";

const ACCENT = "#7c3aed";

// Normalized backend statuses → chip class + label.
const STATUS_CHIP = {
  connected: { cls: "oo-chip--ok", label: "Connected" },
  waiting_approval: { cls: "oo-chip--warn", label: "Waiting approval" },
  rejected: { cls: "oo-chip--danger", label: "Rejected" },
  disabled: { cls: "oo-chip", label: "Disabled" },
};

const TYPE_LABEL = {
  los: "LOS",
  crm: "CRM",
  pricing: "Pricing",
  esign: "E-sign",
  ocr: "OCR",
};

/** Accept ms timestamps or ISO strings; return ms for formatRelative. */
const toMs = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  const parsed = Date.parse(value);

  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Org 360 "Integrations" card. Lists the LOS / CRM / pricing / e-sign / OCR
 * systems this organization is connected to, each with a status badge and last
 * sync time. Only integrations the org has actually engaged with are returned
 * by the backend, so an empty list means "no integrations connected".
 */
export default function IntegrationsSection({ organizationId, env }) {
  const [ref, inView] = useInView();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [integrations, setIntegrations] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchOrgIntegrations(organizationId, env);
      setIntegrations(
        Array.isArray(json?.integrations) ? json.integrations : [],
      );
    } catch (err) {
      setError(err.message || "Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, [organizationId, env]);

  useEffect(() => {
    if (inView) {
      load();
    }
  }, [inView, load]);

  const hasIntegrations = integrations && integrations.length > 0;

  return (
    <SectionCard
      ref={ref}
      title="Integrations"
      subtitle="LOS, CRM & other systems this org is connected to"
      icon={<FaPlug />}
      accent={ACCENT}
      loading={(loading || !inView) && !integrations}
      error={error}
      onRetry={load}
      size="third"
      link={{ to: "/integrations", label: "Manage integrations" }}
    >
      {hasIntegrations ? (
        <div className="oo-integrations-list">
          {integrations.map((integration) => {
            const chip =
              STATUS_CHIP[integration.status] || STATUS_CHIP.disabled;
            const lastSync = toMs(integration.lastSyncedAt);

            return (
              <div key={integration.id} className="oo-integration-row">
                <div className="oo-integration-identity">
                  <span className="oo-integration-name">
                    {integration.name}
                  </span>
                  <span className="oo-integration-meta">
                    {TYPE_LABEL[integration.type] || integration.type}
                    {lastSync ? ` · synced ${formatRelative(lastSync)}` : ""}
                  </span>
                </div>
                <span className={`oo-chip ${chip.cls}`}>{chip.label}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="oo-empty-hint">
          No integrations connected for this org.
        </div>
      )}
    </SectionCard>
  );
}
